import { useCallback, useEffect, useRef } from 'react';
import Phaser from 'phaser';

import { createGameConfig } from '@/features/laneChooserPhaser/game/config';
import { createLaneChooserEventBus, EVENTS } from '@/features/laneChooserPhaser/game/EventBus';

import type { RefObject } from 'react';
import type { LaneChooserEventBus } from '@/features/laneChooserPhaser/game/EventBus';
import type {
	LaneChooserAnswerRequest,
	LaneChooserAnswerResolution,
	LaneChooserGameTheme,
	LaneChooserQuestionPayload,
	LaneChooserSoundEffect,
} from '@/features/laneChooserPhaser/game/types';

const LANE_CHOOSER_GAME_DESTROY_EVENT = Phaser.Core.Events.DESTROY;

type TooltipPoint = { x: number; y: number };

type UseLaneChooserPhaserGameParams = {
	containerRef: RefObject<HTMLDivElement | null>;
	themeConfig: LaneChooserGameTheme;
	laneLinesCount: number;
	roundKey?: number | string;
	question: LaneChooserQuestionPayload | null;
	gameStarted: boolean;
	answerResolution: LaneChooserAnswerResolution | null;
	onAnswerRequest?: (selectedIndex: number) => void;
	onRoundExitComplete?: () => void;
	onSoundEffect?: (effect: LaneChooserSoundEffect) => void;
	onInstructionComplete?: () => void;
	onInstructionTooltipChange?: (textKey: string | null) => void;
	onInstructionTooltipPositionChange?: (point: TooltipPoint | null) => void;
	onQuestionImagePreview?: (imageSrc: string) => void;
};

type UseLaneChooserPhaserGameResult = {
	skipInstructions: () => void;
};

let pendingLaneChooserGameDestroy: Promise<void> | null = null;

function syncGameCanvasSize(game: Phaser.Game, container: HTMLElement): void {
	const width = Math.max(1, Math.round(container.clientWidth || window.innerWidth || 1));
	const height = Math.max(1, Math.round(container.clientHeight || window.innerHeight || 1));
	const devicePixelRatio = window.devicePixelRatio || 1;

	game.canvas.style.width = `${width}px`;
	game.canvas.style.height = `${height}px`;
	game.scale.resize(
		Math.max(1, Math.round(width * devicePixelRatio)),
		Math.max(1, Math.round(height * devicePixelRatio)),
	);
}

function releaseWebGlContext(game: Phaser.Game): void {
	if (game.renderer.type !== Phaser.WEBGL) {
		return;
	}

	const renderer = game.renderer as Phaser.Renderer.WebGL.WebGLRenderer;
	renderer.gl?.getExtension('WEBGL_lose_context')?.loseContext();
}

function destroyLaneChooserGame(game: Phaser.Game, eventBus: LaneChooserEventBus): void {
	let resolveDestroy: () => void = () => undefined;
	const destroyPromise = new Promise<void>((resolve) => {
		resolveDestroy = resolve;
	});

	pendingLaneChooserGameDestroy = destroyPromise;
	game.events.once(LANE_CHOOSER_GAME_DESTROY_EVENT, () => {
		eventBus.removeAllListeners();
		if (pendingLaneChooserGameDestroy === destroyPromise) {
			pendingLaneChooserGameDestroy = null;
		}
		resolveDestroy();
	});

	releaseWebGlContext(game);
	game.destroy(true);
}

export function useLaneChooserPhaserGame(params: UseLaneChooserPhaserGameParams): UseLaneChooserPhaserGameResult {
	const gameRef = useRef<Phaser.Game | null>(null);
	const eventBusRef = useRef<LaneChooserEventBus | null>(null);
	const sceneReadyRef = useRef(false);
	const worldLoadedRef = useRef(false);
	const latestParamsRef = useRef(params);

	latestParamsRef.current = params;

	useEffect(() => {
		let isMounted = true;
		let resizeObserver: ResizeObserver | null = null;
		let removeWindowResizeListener: (() => void) | null = null;

		const mountGame = async () => {
			if (pendingLaneChooserGameDestroy) {
				await pendingLaneChooserGameDestroy;
			}

			const container = latestParamsRef.current.containerRef.current;
			if (!isMounted || !container || gameRef.current) {
				return;
			}

			const eventBus = createLaneChooserEventBus();
			eventBusRef.current = eventBus;

			const emitWorld = () => {
				if (!sceneReadyRef.current || !container.isConnected) {
					return;
				}

				const { laneLinesCount, question, gameStarted, answerResolution } = latestParamsRef.current;
				eventBus.emit(EVENTS.LOAD_WORLD, {
					laneLinesCount,
					viewportWidth: Math.max(1, Math.round(container.clientWidth || window.innerWidth || 1)),
					viewportHeight: Math.max(1, Math.round(container.clientHeight || window.innerHeight || 1)),
					question,
				});
				worldLoadedRef.current = true;
				eventBus.emit(EVENTS.SET_GAME_STARTED, gameStarted);
				if (answerResolution) {
					eventBus.emit(EVENTS.ANSWER_RESOLVED, answerResolution);
				}
			};

			const handleSceneReady = () => {
				if (!gameRef.current) {
					return;
				}

				sceneReadyRef.current = true;
				emitWorld();
			};
			const handleAnswerRequested = ({ selectedIndex }: LaneChooserAnswerRequest) => {
				latestParamsRef.current.onAnswerRequest?.(selectedIndex);
			};
			const handleRoundExitComplete = () => latestParamsRef.current.onRoundExitComplete?.();
			const handleSoundEffect = (effect: LaneChooserSoundEffect) =>
				latestParamsRef.current.onSoundEffect?.(effect);
			const handleInstructionComplete = () => latestParamsRef.current.onInstructionComplete?.();
			const handleInstructionTooltip = (textKey: string | null) => {
				latestParamsRef.current.onInstructionTooltipChange?.(textKey);
			};
			const handleInstructionTooltipPosition = (point: TooltipPoint | null) => {
				latestParamsRef.current.onInstructionTooltipPositionChange?.(point);
			};
			const handleQuestionImagePreview = (imageSrc: string) => {
				latestParamsRef.current.onQuestionImagePreview?.(imageSrc);
			};

			eventBus.on(EVENTS.SCENE_READY, handleSceneReady);
			eventBus.on(EVENTS.ANSWER_REQUESTED, handleAnswerRequested);
			eventBus.on(EVENTS.ROUND_EXIT_COMPLETE, handleRoundExitComplete);
			eventBus.on(EVENTS.SOUND_EFFECT, handleSoundEffect);
			eventBus.on(EVENTS.INSTRUCTION_COMPLETE, handleInstructionComplete);
			eventBus.on(EVENTS.INSTRUCTION_TOOLTIP, handleInstructionTooltip);
			eventBus.on(EVENTS.INSTRUCTION_TOOLTIP_POS, handleInstructionTooltipPosition);
			eventBus.on(EVENTS.QUESTION_IMAGE_PREVIEW, handleQuestionImagePreview);

			const game = new Phaser.Game(createGameConfig(container, eventBus, latestParamsRef.current.themeConfig));
			gameRef.current = game;
			syncGameCanvasSize(game, container);

			const handleResize = () => {
				if (!gameRef.current || !container.isConnected) {
					return;
				}

				syncGameCanvasSize(gameRef.current, container);
				emitWorld();
			};

			if (typeof ResizeObserver === 'undefined') {
				window.addEventListener('resize', handleResize);
				removeWindowResizeListener = () => window.removeEventListener('resize', handleResize);
			} else {
				resizeObserver = new ResizeObserver(handleResize);
				resizeObserver.observe(container);
			}
		};

		mountGame();

		return () => {
			isMounted = false;
			resizeObserver?.disconnect();
			removeWindowResizeListener?.();

			const game = gameRef.current;
			const eventBus = eventBusRef.current;
			if (game && eventBus) {
				eventBus.removeAllListeners();
				destroyLaneChooserGame(game, eventBus);
			} else {
				eventBus?.removeAllListeners();
			}

			gameRef.current = null;
			eventBusRef.current = null;
			sceneReadyRef.current = false;
			worldLoadedRef.current = false;
		};
	}, [params.containerRef]);

	useEffect(() => {
		if (!sceneReadyRef.current || !worldLoadedRef.current) {
			return;
		}

		eventBusRef.current?.emit(EVENTS.UPDATE_ROUND, {
			laneLinesCount: params.laneLinesCount,
			question: params.question,
		});
	}, [params.laneLinesCount, params.question, params.roundKey]);

	useEffect(() => {
		if (!sceneReadyRef.current || !worldLoadedRef.current || !params.answerResolution) {
			return;
		}

		eventBusRef.current?.emit(EVENTS.ANSWER_RESOLVED, params.answerResolution);
	}, [params.answerResolution]);

	useEffect(() => {
		if (!sceneReadyRef.current || !worldLoadedRef.current) {
			return;
		}

		eventBusRef.current?.emit(EVENTS.SET_GAME_STARTED, params.gameStarted);
	}, [params.gameStarted]);

	const skipInstructions = useCallback(() => {
		eventBusRef.current?.emit(EVENTS.SKIP_INSTRUCTIONS);
	}, []);

	return { skipInstructions };
}
